import { FC, useMemo, useCallback } from 'react'
import { Autocomplete, TextField, Chip } from '@mui/material'
import type { FilterOption } from '@/utilities/types/shared-filter'

interface SelectFilterProps {
  field: string
  label: string
  value: string[]
  options?: FilterOption[] | Record<string, FilterOption[]>
  onChange: (field: string, value: string[]) => void
  isMulti?: boolean
  tab?: string
}

export const SelectFilter: FC<SelectFilterProps> = ({
  field,
  label,
  value = [],
  options = [],
  onChange,
  isMulti = true,
  tab,
}) => {
  const processedOptions = useMemo(() => {
    if (!options) return []
    if (Array.isArray(options)) return options
    if (tab && options[tab]) return options[tab]
    return []
  }, [options, tab])

  // Helper function to ensure exact matching for region field
  const handleExactMatch = useCallback(
    (selectedValues: string[]) => {
      if (field === 'region') {
        // For region field, we want exact matches
        return selectedValues.map((value) => value.trim()) // Ensure no whitespace
      }
      return selectedValues
    },
    [field],
  )

  const handleChange = (
    _: any,
    newValue: FilterOption | FilterOption[] | null,
  ) => {
    if (Array.isArray(newValue)) {
      const values = newValue.map((option) => option.value)
      const processedValues = handleExactMatch(values)
      onChange(field, processedValues)
    } else if (newValue) {
      const values = [newValue.value]
      const processedValues = handleExactMatch(values)
      onChange(field, processedValues)
    } else {
      onChange(field, [])
    }
  }

  // Filter function to ensure exact matching for regions
  const filterOptions = useCallback(
    (options: FilterOption[], { inputValue }: { inputValue: string }) => {
      if (field === 'region') {
        // For region field, only show options that match exactly or start with the input
        return options.filter(
          (option) =>
            option.label.toLowerCase().startsWith(inputValue.toLowerCase()) ||
            option.value.toLowerCase().startsWith(inputValue.toLowerCase()),
        )
      }
      // For other fields, use default filtering
      return options.filter(
        (option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
          option.value.toLowerCase().includes(inputValue.toLowerCase()),
      )
    },
    [field],
  )

  return (
    <Autocomplete
      multiple={isMulti}
      options={processedOptions}
      value={processedOptions.filter((option) => value.includes(option.value))}
      onChange={handleChange}
      filterOptions={filterOptions}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder={`Select ${label}`}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.value}
            label={option.label}
            size="small"
          />
        ))
      }
      disableCloseOnSelect={isMulti}
      sx={{
        '& .MuiOutlinedInput-root': {
          padding: '3px 8px',
        },
      }}
    />
  )
}
